'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  CalculatorIcon,
  FileTextIcon,
  HashIcon,
  ImagesIcon,
  MessageCircleMoreIcon,
  RefreshCcwIcon,
  TrashIcon,
  WaypointsIcon,
  CopyIcon
} from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  ConfirmationDialog,
  ContentDetailFile,
  ContentDetailInfo,
  ContentError,
  DeleteConfirmationDialog,
  JobStatusBadge,
  ProductImportEffectedItems
} from '@/components/partials'
import {
  useDeleteProductPictureImport,
  useProductPictureImport,
  useRetryProductPictureImport,
  useCopyToClipboard
} from '@/hooks'
import { formatNumber, tOptional } from '@/lib/utils'
import { JobStatusEnum } from '@/enums'

type PropsType = {
  id: string
}

const ProductPictureImportShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteProductPictureImportId, setProductPictureImportId] = useState<string | null>(null)
  const [retryConfirmOpen, setRetryConfirmOpen] = useState<boolean>(false)

  const { data: productPictureImport, isLoading, error } = useProductPictureImport(id)
  const { mutate: retryProductPictureImport, isPending: isRetrying } = useRetryProductPictureImport()
  const { mutate: deleteProductPictureImport, isPending: isDeleting } = useDeleteProductPictureImport()

  const handleRetry = () => {
    setRetryConfirmOpen(true)
  }

  const confirmRetry = () => {
    retryProductPictureImport(id, {
      onSuccess: async newProductPictureImport => {
        await queryClient.invalidateQueries({ queryKey: ['productPictureImports'] })
        toast.success(t('retry_started'))
        setRetryConfirmOpen(false)
        router.push(`/products/import/picture/${newProductPictureImport.id}`)
      },
      onError: (error: any) => {
        console.error('Error retrying productPictureImport:', error)
        toast.error(t('error_retrying'))
        setRetryConfirmOpen(false)
      }
    })
  }

  const handleDelete = () => {
    setProductPictureImportId(id)
  }

  const confirmDeleteProductPictureImport = () => {
    if (deleteProductPictureImportId) {
      deleteProductPictureImport(deleteProductPictureImportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productPictureImports'] })
          toast.success(t('deleted_successfully'))
          router.push('/products/import/picture')
          setProductPictureImportId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting productPictureImport:', error)
          toast.error(t('error_deleting'))
          setProductPictureImportId(null)
        }
      })
    }
  }

  // Show loading skeleton only when initially loading or during delete/retry operations
  // Don't show skeleton if we're just refetching and already have data
  if ((isLoading && !productPictureImport) || isDeleting || isRetrying) {
    return <ProductPictureImportShowSkeleton />
  }

  // Only show an error if there's an error, and we don't have any data to show
  if (error && !productPictureImport) {
    return <ContentError error={error} listUrl="/products/import/picture" />
  }

  // If no data and not loading, show an error
  if (!productPictureImport) {
    return <ContentError error={error} listUrl="/products/import/picture" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/products/import/picture">
              <ArrowLeftIcon className="h-4 w-4" />
              {t('list')}
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('bulk_picture_import_details')}</h1>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          {productPictureImport.status === JobStatusEnum.FAILED && (
            <Button variant="outline" onClick={handleRetry} disabled={isRetrying}>
              <RefreshCcwIcon className="h-4 w-4" />
              <span className="hidden items-center gap-2 lg:flex">{t('retry')}</span>
            </Button>
          )}
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      {/* Product Picture Import Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {productPictureImport.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(productPictureImport.id)}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <WaypointsIcon className="h-4 w-4" />
                {t('status')}
              </div>
              <JobStatusBadge status={productPictureImport.status} />
            </div>
            <div className="col-span-5 space-y-2 lg:col-span-4 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <MessageCircleMoreIcon className="h-4 w-4" />
                {t('message')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {tOptional(t, productPictureImport.message ?? '-')}
              </Badge>
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={productPictureImport} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImagesIcon className="h-5 w-5" />
            {t('processed_files')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="col-span-1 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalculatorIcon className="h-4 w-4" />
                {t('created_items')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {formatNumber(productPictureImport.successfulFiles, locale)}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalculatorIcon className="h-4 w-4" />
                {t('failed_items')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {formatNumber(productPictureImport.failedFiles, locale)}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalculatorIcon className="h-4 w-4" />
                {t('total')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {formatNumber(productPictureImport.processedFiles, locale)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Created Items */}
      <ProductImportEffectedItems title={t('created_items')} items={productPictureImport.createdItems} />

      {/* Updated Items */}
      <ProductImportEffectedItems title={t('failed_items')} items={productPictureImport.failedItems} />

      {/* Files */}
      <ContentDetailFile file={productPictureImport.file} />

      <DeleteConfirmationDialog
        open={deleteProductPictureImportId !== null}
        onCancel={() => setProductPictureImportId(null)}
        onConfirm={confirmDeleteProductPictureImport}
        isDeleting={isDeleting}
      />

      <ConfirmationDialog
        open={retryConfirmOpen}
        onCancel={() => setRetryConfirmOpen(false)}
        onConfirm={confirmRetry}
        isLoading={isRetrying}
        description={t('retry_confirmation_message')}
        confirmText={t('yes')}
        cancelText={t('no')}
        loadingText={t('retrying')}
      />
    </div>
  )
}

export default Detail
